/**
 * Автозаполнение посевного плана — правила и подбор по севообороту (этап 2).
 *
 * Вся логика здесь чистая и без запросов: именно она решает, что окажется на
 * двух сотнях полей после одного нажатия. Ошибка в ней — это не кривая
 * вёрстка, а неверный план на весь сезон, поэтому она проверяется таблично,
 * а не рендером.
 *
 * Общий принцип обоих механизмов: **автозаполнение только ДОБАВЛЯЕТ**. Оно
 * никогда не трогает поля, где культура уже стоит, — ни своей рукой
 * поставленная, ни пар. Иначе одно нажатие затирало бы работу, которую
 * агроном делал вручную полчаса, и к кнопке никто больше не подошёл бы.
 */
import type { VariantField } from '@/lib/planning/planCrops';
import { isUnassigned, UNKNOWN_HISTORY } from './sowingPlan';

// ── А. Правила «если предшественник → сеем» ──────────────────────────────────

export interface FillRule {
  id: string;
  /** Предшественник (факт прошлого сезона). `null` — «любой». */
  predecessorCropId: string | null;
  /** Препредшественник. `null` — «любой»; условие необязательное. */
  predecessor2CropId: string | null;
  /** Что сеем, если правило подошло. */
  cropId: string;
}

export interface RulePreviewRow {
  ruleId: string;
  /** Сколько полей ЗАБЕРЁТ это правило с учётом того, что выше уже забрали. */
  fields: number;
  areaHa: number;
}

export interface RulesPreview {
  rows: RulePreviewRow[];
  /** Итог: сколько свободных полей заполнится. */
  totalFields: number;
  totalAreaHa: number;
  /** Свободных полей всего — знаменатель «K из N». */
  freeFields: number;
  /** Поле → культура, которую поставит применение. */
  assignment: Map<string, string>;
}

/**
 * Условие правила по одной ступени истории.
 *
 * `null` — «любой». `UNKNOWN_HISTORY` — «нет данных»: выпадашка правила
 * предлагает её так же, как фильтр таблицы, и значить она обязана то же
 * самое — поля БЕЗ истории. Сравнивать маркер с crop_id напрямую нельзя:
 * правило «нет данных → сеем X» тогда не подошло бы ни одному полю вовсе.
 */
function historyConditionMatches(fieldCropId: string | null, ruleCropId: string | null): boolean {
  if (!ruleCropId) return true;
  if (ruleCropId === UNKNOWN_HISTORY) return fieldCropId === null;
  return fieldCropId === ruleCropId;
}

function ruleMatches(field: VariantField, rule: FillRule): boolean {
  return (
    historyConditionMatches(field.predecessor_crop_id, rule.predecessorCropId) &&
    historyConditionMatches(field.predecessor2_crop_id, rule.predecessor2CropId)
  );
}

/**
 * Применить правила СВЕРХУ ВНИЗ к свободным полям.
 *
 * Первое подошедшее правило забирает поле насовсем: следующие его уже не
 * видят. Так порядок в списке становится приоритетом — ровно то, чего ждёт
 * человек, читающий правила сверху вниз. Если бы побеждало последнее, список
 * пришлось бы читать задом наперёд, а частное правило вверху молча
 * перебивалось бы общим внизу.
 */
export function previewRules(
  fields: readonly VariantField[],
  rules: readonly FillRule[],
): RulesPreview {
  const free = fields.filter(isUnassigned);
  const assignment = new Map<string, string>();
  const taken = new Set<string>();
  const rows: RulePreviewRow[] = [];

  for (const rule of rules) {
    let count = 0;
    let areaHa = 0;
    for (const field of free) {
      if (taken.has(field.id)) continue;
      if (!ruleMatches(field, rule)) continue;
      taken.add(field.id);
      assignment.set(field.id, rule.cropId);
      count += 1;
      areaHa += field.area_ha;
    }
    rows.push({ ruleId: rule.id, fields: count, areaHa });
  }

  let totalAreaHa = 0;
  for (const field of free) {
    if (assignment.has(field.id)) totalAreaHa += field.area_ha;
  }

  return {
    rows,
    totalFields: assignment.size,
    totalAreaHa,
    freeFields: free.length,
    assignment,
  };
}

// ── Б. Подбор по севообороту ────────────────────────────────────────────────

export interface RotationHistoryEntry {
  year: number;
  key: string | null;
  name: string;
  is_fallow: boolean;
}

export interface RotationField {
  field_id: string;
  name: string;
  area_ha: number;
  history: RotationHistoryEntry[];
  match_depth: number;
  compare_depth: number;
  suggested_crop_id: string | null;
  suggested_name: string | null;
  suggested_is_fallow: boolean;
  suggested_years_since: number | null;
}

export interface ReturnNorm {
  crop_id: string;
  name: string;
  min_return_years: number;
}

export interface RotationMatch {
  scenario_id: string;
  season_year: number;
  ring: { key: string; name: string; is_fallow: boolean }[];
  return_norms: ReturnNorm[];
  fields: RotationField[];
  occupied_fields: number;
}

/** Куда попало поле после подбора. Названия — как в макете v2-15. */
export type RotationBucket = 'full' | 'partial' | 'weak';

export interface BucketedField {
  field: RotationField;
  bucket: RotationBucket;
  /** Почему культура НЕ будет подставлена, хотя кольцо её предложило. */
  blockedReason: string | null;
}

export interface RotationPlan {
  full: BucketedField[];
  partial: BucketedField[];
  weak: BucketedField[];
  /** Поле → культура: то, что реально уйдёт в применение. */
  assignment: Map<string, string>;
  totalFields: number;
}

/**
 * Разложить поля по трём корзинам с учётом нормативов возврата.
 *
 * Норматив приходит из справочника культур, но экран даёт править его НА
 * СЕССИЮ применения — поэтому фильтр живёт здесь, а не на бэке: подбор
 * пересчитывается мгновенно, без похода на сервер и без записи в справочник.
 *
 * Нарушение норматива НЕ прячет поле и не отменяет подбор — оно убирает
 * культуру из автоподстановки и говорит почему. Решение оставить или
 * переиграть принимает агроном (решение founder по севообороту: система
 * объясняет, но не запрещает).
 */
export function bucketizeRotation(
  match: RotationMatch,
  normOverrides: ReadonlyMap<string, number> = new Map(),
): RotationPlan {
  const norms = new Map<string, number>();
  for (const n of match.return_norms) norms.set(n.crop_id, n.min_return_years);
  for (const [cropId, years] of normOverrides) norms.set(cropId, years);

  const plan: RotationPlan = {
    full: [],
    partial: [],
    weak: [],
    assignment: new Map(),
    totalFields: match.fields.length,
  };

  for (const field of match.fields) {
    const blockedReason = rotationBlockReason(field, norms);
    const bucket = rotationBucket(field, blockedReason !== null);
    const entry: BucketedField = { field, bucket, blockedReason };
    plan[bucket].push(entry);
    // В применение уходят только полные и частичные совпадения с культурой:
    // «слабое» кольцо по решению founder оставляем пустым, а пар посеять
    // нечем — заведение сеет культуру, а не оставляет поле отдыхать.
    if (bucket !== 'weak' && !blockedReason && field.suggested_crop_id) {
      plan.assignment.set(field.field_id, field.suggested_crop_id);
    }
  }
  return plan;
}

/** Почему культуру нельзя подставить автоматически. `null` — можно. */
export function rotationBlockReason(
  field: RotationField,
  norms: ReadonlyMap<string, number>,
): string | null {
  if (field.suggested_is_fallow) {
    // Пар — состояние поля, а не культура: массовое заведение его не поставит.
    return 'кольцо ведёт к пару — его ставят вручную';
  }
  if (!field.suggested_crop_id) return null;
  const norm = norms.get(field.suggested_crop_id);
  const since = field.suggested_years_since;
  if (norm && since !== null && since < norm) {
    return `${field.suggested_name} тут росла ${since} ${yearsWord(since)} назад, норматив возврата — ${norm}`;
  }
  return null;
}

/**
 * Корзина поля.
 *
 * `full` — история легла на кольцо целиком (на всю доступную глубину);
 * `partial` — совпало частично, культуру предлагаем с пометкой;
 * `weak` — не совпало, нечего предложить или предложение заблокировано.
 */
export function rotationBucket(field: RotationField, blocked = false): RotationBucket {
  if (blocked || field.match_depth === 0 || field.compare_depth === 0) return 'weak';
  if (!field.suggested_crop_id && !field.suggested_is_fallow) return 'weak';
  return field.match_depth >= field.compare_depth ? 'full' : 'partial';
}

/** «3 года» / «1 год» / «5 лет» — агроном читает текст, а не число с точкой. */
export function yearsWord(years: number): string {
  const n = Math.abs(years) % 100;
  const last = n % 10;
  if (n > 10 && n < 20) return 'лет';
  if (last === 1) return 'год';
  if (last >= 2 && last <= 4) return 'года';
  return 'лет';
}

/** Подпись глубины совпадения: «совпало 2 из 3». */
export function matchDepthText(field: RotationField): string {
  if (field.compare_depth === 0) return 'истории нет';
  if (field.match_depth === 0) return 'история не совпала с кольцом';
  if (field.match_depth >= field.compare_depth) {
    return `совпало полностью (${field.match_depth} из ${field.compare_depth})`;
  }
  return `совпало ${field.match_depth} из ${field.compare_depth}`;
}

// ── Применение: превращаем раскладку в массовые запросы ─────────────────────

export interface FillBatch {
  cropId: string;
  fieldIds: string[];
}

/**
 * Сгруппировать раскладку по культурам.
 *
 * Заведение культуры принимает СПИСОК полей, поэтому на всё автозаполнение
 * уходит столько запросов, сколько разных культур (обычно 3-5), а не по
 * запросу на поле. Порядок культур стабилен — чтобы итог не «прыгал» между
 * одинаковыми прогонами.
 */
export function toFillBatches(assignment: ReadonlyMap<string, string>): FillBatch[] {
  const byCrop = new Map<string, string[]>();
  for (const [fieldId, cropId] of assignment) {
    const bucket = byCrop.get(cropId) ?? [];
    bucket.push(fieldId);
    byCrop.set(cropId, bucket);
  }
  return [...byCrop.entries()]
    .map(([cropId, fieldIds]) => ({ cropId, fieldIds: [...fieldIds].sort() }))
    .sort((a, b) => a.cropId.localeCompare(b.cropId));
}
